import React, { FormEvent, useCallback, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Colors, Theme } from '@app/theme';
import {
  CSMRecommendationType,
  useCreateCSMRecommendation,
  useCSMRecommendations,
} from '@app/api';
import {
  useToast,
  SessionBoxInfo,
  useBoolModalState,
  SquareButton,
  TextArea,
  Button,
} from 'components';
import { RecommendationClient } from '../RecommendationClient';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  textArea: {
    marginBottom: theme.marginBase,
  },
  sumbitLine: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.marginBase * 2,
    gap: theme.marginBase,
  },
}));

interface Props {
  sessionId?: string;
  className?: string;
}

const types = [
  {
    name: 'error',
    color: 'red',
    recommendationType: CSMRecommendationType.ERROR,
  },
  {
    name: 'warn',
    color: 'yellow',
    recommendationType: CSMRecommendationType.WARN,
  },
  {
    name: 'info',
    color: 'blue',
    recommendationType: CSMRecommendationType.INFO,
  },
];

export const RecommendationCard = ({ sessionId, className }: Props) => {
  const classes = useStyles();
  const toast = useToast();
  const { t } = useTranslation();
  const { data: recommendations } = useCSMRecommendations(sessionId);

  const { mutateAsync: addRecommendation } =
    useCreateCSMRecommendation(sessionId);

  const [adding, open, close] = useBoolModalState();
  const [reco, setReco] = useState('');

  const sendReco = useCallback(
    async (message: string, type: CSMRecommendationType) => {
      try {
        if (!sessionId) {
          return;
        }
        await addRecommendation({
          message,
          sessionId,
          type,
        });
        close();
      } catch (e) {
        toast.exception(e);
      }
    },
    [reco, close],
  );

  return (
    <SessionBoxInfo
      color="yellow"
      title={t('sessions.details.boxes.tips')}
      className={className}
      icon="ri-notification-2-line"
      actionButton={
        <SquareButton
          onClick={adding ? close : open}
          color="yellow"
          icon="ri-add-line"
        />
      }
    >
      {!!adding && (
        <div>
          <TextArea
            containerClass={classes.textArea}
            onChange={(e) => setReco(e.currentTarget.value)}
          />
          <div className={classes.sumbitLine}>
            {types.map((type) => (
              <Button
                key={type.name}
                color={type.color as Colors}
                small
                text={t(`eventHistory.recommendationButton.${type.name}`)}
                onClick={() => sendReco(reco, type.recommendationType)}
              />
            ))}
          </div>
        </div>
      )}

      {!!recommendations && !!sessionId && (
        <RecommendationClient
          recommendations={recommendations}
          sessionId={sessionId}
          allowHide={false}
        />
      )}
    </SessionBoxInfo>
  );
};
